import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useActiveProgramId } from "./programs/program-scope";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { TransactionsApi } from "@/api/perkpoint/transactions";
import { apiErrorMessage, isNotFound } from "@/api/perkpoint/client";
import {
  MembersApi,
  initialsOf,
  memberDisplayName,
  memberKeys,
  relativeDays,
  titleCase,
  type ApiMemberDetail,
} from "@/api/perkpoint/members";
import { AdjustMemberPointsDialog } from "./AdjustMemberPointsDialog";
import { ChartExportButton, StartEndDates } from "./chart-controls";
import { monthsAgoWindow, monthsInRange } from "./chart-dates";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  ChevronRight,
  Clock,
  Coins,
  Copy,
  Crown,
  Download,
  Gamepad2,
  Gift,
  Megaphone,
  MoreVertical,
  Pencil,
  ShieldCheck,
  Ticket,
  Users,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

type MemberTier = string;
type MemberGender = "Female" | "Male" | "Other" | "Prefer not to say";
type PreferredLanguage = "English" | "Arabic" | "Hindi" | "French";

const GENDER_OPTIONS: MemberGender[] = [
  "Female",
  "Male",
  "Other",
  "Prefer not to say",
];

const PREFERRED_LANGUAGE_OPTIONS: PreferredLanguage[] = [
  "English",
  "Arabic",
  "Hindi",
  "French",
];

type TierStep = { name: MemberTier; key: string; threshold: number; badgeClass: string };

const TIER_BADGES = [
  "bg-orange-50 text-orange-800",
  "bg-slate-100 text-slate-700",
  "bg-amber-50 text-amber-800",
  "bg-slate-800 text-white",
  "bg-violet-50 text-violet-800",
];

const GENDER_FROM_API: Record<string, MemberGender> = {
  female: "Female",
  male: "Male",
  other: "Other",
  prefer_not_to_say: "Prefer not to say",
};
const GENDER_TO_API: Record<MemberGender, string> = {
  Female: "female",
  Male: "male",
  Other: "other",
  "Prefer not to say": "prefer_not_to_say",
};
const LANGUAGE_FROM_API: Record<string, PreferredLanguage> = {
  en: "English",
  ar: "Arabic",
  hi: "Hindi",
  fr: "French",
};

function formatLongDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** The profile shape this page renders, built from the API member view. */
function profileFromApi(member: ApiMemberDetail) {
  const consent = (channel: string) =>
    member.consents.some((c) => c.channel === channel && c.status === "opted_in");
  const risk =
    member.risk === "blocked" ? "Blocked" : member.risk === "at_risk" ? "At risk" : "Low risk";
  const displayName = memberDisplayName(member) || "Unnamed member";
  return {
    name: displayName,
    email: member.email ?? "",
    phone: member.phone ?? "",
    memberId: member.external_member_id,
    initials: initialsOf(displayName),
    status: titleCase(member.display_status),
    tier: member.tier?.name ?? "—",
    tierKey: member.tier?.key ?? "",
    pointsBalance: member.points.available,
    pointsPending: member.points.pending,
    pointsEarned: member.points.lifetime_earned,
    pointsRedeemed: member.points.lifetime_redeemed,
    expiringSoon: member.points.expiring_soon,
    engagement: titleCase(member.engagement),
    memberSince: formatLongDate(member.member_since ?? member.enrolled_at),
    lastActivity: relativeDays(member.last_activity_at),
    rewardRedemptions: member.reward_redemptions,
    campaignParticipation: member.campaign_participation,
    primarySegment: member.primary_segment?.name ?? member.segment?.name ?? "None yet",
    risk,
    nextAction: member.next_action,
    gender: (member.gender ? GENDER_FROM_API[member.gender] ?? "" : "") as MemberGender | "",
    dateOfBirth: member.date_of_birth?.slice(0, 10) ?? "",
    anniversary: member.anniversary?.slice(0, 10) ?? "",
    city: member.city ?? "",
    preferredLanguage: (member.preferred_language
      ? LANGUAGE_FROM_API[member.preferred_language] ?? ""
      : "") as PreferredLanguage | "",
    consentWhatsapp: consent("whatsapp"),
    consentSms: consent("sms"),
    consentEmail: consent("email"),
  };
}

function getTierMeta(ladder: TierStep[], tier: MemberTier, pointsBalance: number) {
  const fallback: TierStep = { name: tier, key: "", threshold: 0, badgeClass: TIER_BADGES[0] };
  const index = ladder.findIndex((item) => item.name === tier);
  const current = ladder[index] ?? fallback;
  const next = index >= 0 ? ladder[index + 1] : undefined;
  if (!next) {
    return {
      ...current,
      nextTier: tier,
      nextTierPoints: current.threshold,
      pointsToNextTier: 0,
      isTop: true,
    };
  }
  return {
    ...current,
    nextTier: next.name,
    nextTierPoints: next.threshold,
    pointsToNextTier: Math.max(0, next.threshold - pointsBalance),
    isTop: false,
  };
}

type MemberProfile = ReturnType<typeof profileFromApi>;

type MemberDetailsDraft = Pick<
  MemberProfile,
  | "name"
  | "email"
  | "phone"
  | "gender"
  | "dateOfBirth"
  | "anniversary"
  | "city"
  | "preferredLanguage"
  | "consentWhatsapp"
  | "consentSms"
  | "consentEmail"
>;

function detailsDraftFrom(profile: MemberProfile): MemberDetailsDraft {
  return {
    name: profile.name,
    email: profile.email,
    phone: profile.phone,
    gender: profile.gender,
    dateOfBirth: profile.dateOfBirth,
    anniversary: profile.anniversary,
    city: profile.city,
    preferredLanguage: profile.preferredLanguage,
    consentWhatsapp: profile.consentWhatsapp,
    consentSms: profile.consentSms,
    consentEmail: profile.consentEmail,
  };
}

function initialsFromName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatProfileDate(iso: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return "";
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function isFutureDate(iso: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return true;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  if (Number.isNaN(date.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date > today;
}

function consentSummary(profile: MemberProfile) {
  const channels = [
    profile.consentWhatsapp ? "WhatsApp" : "",
    profile.consentSms ? "SMS" : "",
    profile.consentEmail ? "Email" : "",
  ].filter(Boolean);
  return channels.length > 0 ? channels.join(", ") : "No marketing consent";
}

function TierProgress({
  pointsBalance,
  pointsToNextTier,
  nextTier,
  nextTierPoints,
  isTop,
  className,
  tone = "light",
}: {
  pointsBalance: number;
  pointsToNextTier: number;
  nextTier: string;
  nextTierPoints: number;
  isTop?: boolean;
  className?: string;
  tone?: "light" | "dark";
}) {
  const progress = isTop
    ? 100
    : nextTierPoints === 0
      ? 0
      : Math.min(100, Math.round((pointsBalance / nextTierPoints) * 100));
  const onDark = tone === "dark";

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span
          className={cn(
            "font-medium",
            onDark ? "text-emerald-50" : "text-slate-700",
          )}
        >
          {isTop
            ? "Top tier reached"
            : `${pointsToNextTier.toLocaleString("en-IN")} pts to ${nextTier}`}
        </span>
      </div>
      <div
        className={cn(
          "h-2.5 overflow-hidden rounded-full",
          onDark ? "bg-white/15" : "bg-slate-100",
        )}
      >
        <div
          className={cn(
            "h-full rounded-full",
            onDark ? "bg-[#E7F6EE]" : "bg-[#1B7A45]",
          )}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div
        className={cn(
          "flex items-center justify-between text-sm",
          onDark ? "text-emerald-50/70" : "text-muted-foreground",
        )}
      >
        <span>
          {pointsBalance.toLocaleString("en-IN")}
          {isTop ? " pts" : ` / ${nextTierPoints.toLocaleString("en-IN")} pts`}
        </span>
        <span>{nextTier}</span>
      </div>
    </div>
  );
}

function MemberDetailsForm({
  draft,
  onChange,
}: {
  draft: MemberDetailsDraft;
  onChange: (next: MemberDetailsDraft) => void;
}) {
  const setField = <K extends keyof MemberDetailsDraft>(
    key: K,
    value: MemberDetailsDraft[K],
  ) => onChange({ ...draft, [key]: value });

  return (
    <div className="mt-6 space-y-6">
      <section className="space-y-4">
        <h3 className="text-base font-semibold text-slate-900">Contact</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="member-name">
              Full name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="member-name"
              value={draft.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Enter full name"
              className="h-10 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="member-phone">Mobile number</Label>
            <Input
              id="member-phone"
              value={draft.phone}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder="Enter mobile number"
              className="h-10 bg-white"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="member-email">Email</Label>
            <Input
              id="member-email"
              type="email"
              value={draft.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="Enter email"
              className="h-10 bg-white"
            />
          </div>
        </div>
      </section>

      <section className="space-y-4 border-t border-slate-200 pt-5">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Profile for programs and campaigns
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Optional today. Fill what you know so later offers can match the
            member.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="member-gender">Gender</Label>
            <Select
              value={draft.gender || undefined}
              onValueChange={(value) =>
                setField("gender", value as MemberGender)
              }
            >
              <SelectTrigger id="member-gender" className="h-10 bg-white">
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="member-language">Preferred language</Label>
            <Select
              value={draft.preferredLanguage || undefined}
              onValueChange={(value) =>
                setField("preferredLanguage", value as PreferredLanguage)
              }
            >
              <SelectTrigger id="member-language" className="h-10 bg-white">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {PREFERRED_LANGUAGE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="member-dob">Date of birth</Label>
            <Input
              id="member-dob"
              type="date"
              value={draft.dateOfBirth}
              onChange={(e) => setField("dateOfBirth", e.target.value)}
              className="h-10 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="member-anniversary">Anniversary</Label>
            <Input
              id="member-anniversary"
              type="date"
              value={draft.anniversary}
              onChange={(e) => setField("anniversary", e.target.value)}
              className="h-10 bg-white"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="member-city">City or area</Label>
            <Input
              id="member-city"
              value={draft.city}
              onChange={(e) => setField("city", e.target.value)}
              placeholder="Enter city or area"
              className="h-10 bg-white"
            />
          </div>
        </div>
      </section>

      <section className="space-y-3 border-t border-slate-200 pt-5">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Marketing consent
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Campaigns can message the member only on channels they have agreed
            to.
          </p>
        </div>
        {(
          [
            ["consentWhatsapp", "WhatsApp", "Offers and campaign updates on WhatsApp."],
            ["consentSms", "SMS", "Offers and campaign updates by SMS."],
            ["consentEmail", "Email", "Offers and campaign updates by email."],
          ] as const
        ).map(([key, label, hint]) => (
          <label
            key={key}
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3"
          >
            <Checkbox
              checked={draft[key]}
              onCheckedChange={(checked) => setField(key, Boolean(checked))}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium text-slate-900">
                {label}
              </span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                {hint}
              </span>
            </span>
          </label>
        ))}
      </section>
    </div>
  );
}

const ACTIVITY_ICON: Record<string, { icon: typeof Gift; className: string; category: string }> = {
  earn: { icon: Coins, className: "bg-green-50 text-green-700", category: "Points earned" },
  redeem: { icon: Gift, className: "bg-emerald-50 text-emerald-700", category: "Reward redeemed" },
  adjust: { icon: Pencil, className: "bg-sky-50 text-sky-700", category: "Adjustment" },
  expire: { icon: Clock, className: "bg-slate-100 text-slate-600", category: "Expired" },
  reverse: { icon: ArrowLeft, className: "bg-orange-50 text-orange-700", category: "Reversal" },
};

export default function MemberDetails() {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { programId } = useActiveProgramId();
  const pid = programId ?? "";
  const ready = Boolean(programId && id);

  const memberQuery = useQuery({
    queryKey: memberKeys.detail(pid, id),
    queryFn: () => MembersApi.get(pid, id),
    enabled: ready,
  });
  const programQuery = useQuery({
    queryKey: programKeys.detail(pid),
    queryFn: () => ProgramsApi.get(pid),
    enabled: Boolean(programId),
  });
  const [activityType, setActivityType] = useState("all");
  const activityQuery = useQuery({
    queryKey: memberKeys.sub(pid, id, "activity", activityType),
    queryFn: () =>
      TransactionsApi.forMember(pid, id, {
        pageSize: 6,
        type: activityType === "all" ? undefined : activityType,
      }),
    enabled: ready,
  });
  const initialOverview = monthsAgoWindow(6);
  const [overviewFrom, setOverviewFrom] = useState(initialOverview.from);
  const [overviewTo, setOverviewTo] = useState(initialOverview.to);
  const overviewMonths = monthsInRange(overviewFrom, overviewTo);
  const overviewQuery = useQuery({
    queryKey: memberKeys.sub(pid, id, "points-overview", String(overviewMonths)),
    queryFn: () => MembersApi.pointsOverview(pid, id, overviewMonths),
    enabled: ready,
  });
  const redemptionsQuery = useQuery({
    queryKey: memberKeys.sub(pid, id, "redemptions"),
    queryFn: () => MembersApi.redemptions(pid, id, 5),
    enabled: ready,
  });
  const participationQuery = useQuery({
    queryKey: memberKeys.sub(pid, id, "participation"),
    queryFn: () => MembersApi.participation(pid, id),
    enabled: ready,
  });

  const member = memberQuery.data;
  const memberProfile = useMemo(
    () => (member ? profileFromApi(member) : null),
    [member],
  );
  const ladder: TierStep[] = useMemo(
    () =>
      (programQuery.data?.tiers ?? [])
        .slice()
        .sort((x, y) => x.rank - y.rank)
        .map((tier, index) => ({
          name: tier.name,
          key: tier.key,
          threshold: tier.entry_points ?? 0,
          badgeClass: TIER_BADGES[Math.min(index, TIER_BADGES.length - 1)],
        })),
    [programQuery.data],
  );

  const [tierSheetOpen, setTierSheetOpen] = useState(false);
  const [pointsSheetOpen, setPointsSheetOpen] = useState(false);
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState<MemberDetailsDraft | null>(null);
  const [selectedTier, setSelectedTier] = useState<MemberTier>("");
  const [tierChangeReason, setTierChangeReason] = useState("");

  // "Edit member" in the members list opens the details sheet straight away.
  const [editHandled, setEditHandled] = useState(false);
  useEffect(() => {
    if (!editHandled && memberProfile && searchParams.get("edit") === "1") {
      setDetailsDraft(detailsDraftFrom(memberProfile));
      setDetailsSheetOpen(true);
      setEditHandled(true);
    }
  }, [editHandled, memberProfile, searchParams]);

  const refreshMember = (next?: ApiMemberDetail) => {
    if (next) queryClient.setQueryData(memberKeys.detail(pid, id), next);
    queryClient.invalidateQueries({ queryKey: memberKeys.all });
  };

  const updateMutation = useMutation({
    mutationFn: (draft: MemberDetailsDraft) =>
      MembersApi.update(pid, id, {
        full_name: draft.name.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim(),
        gender: draft.gender ? GENDER_TO_API[draft.gender] : "",
        date_of_birth: draft.dateOfBirth,
        anniversary: draft.anniversary,
        city: draft.city.trim(),
        preferred_language: draft.preferredLanguage,
        consents: {
          whatsapp: draft.consentWhatsapp,
          sms: draft.consentSms,
          email: draft.consentEmail,
        },
      }),
    onSuccess: (updated) => {
      refreshMember(updated);
      toast.success("Member details updated");
      setDetailsSheetOpen(false);
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not update the member")),
  });

  const tierMutation = useMutation({
    mutationFn: (input: { tierKey: string; reason: string }) =>
      MembersApi.changeTier(pid, id, {
        tier_key: input.tierKey,
        reason: input.reason || undefined,
        after_change: "standard",
      }),
    onSuccess: (result, input) => {
      refreshMember();
      const name = ladder.find((t) => t.key === input.tierKey)?.name ?? input.tierKey;
      const verb = result.direction === "downgrade" ? "downgraded" : "upgraded";
      toast.success(
        `${memberProfile?.name ?? "Member"} ${verb} to ${name}${input.reason ? ` · ${input.reason}` : ""}`,
      );
      setTierSheetOpen(false);
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not change the tier")),
  });

  if (!memberProfile) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 py-10">
        <p className="text-sm text-muted-foreground">
          {!programId || memberQuery.isLoading
            ? "Loading member…"
            : memberQuery.isError && !isNotFound(memberQuery.error)
              ? apiErrorMessage(memberQuery.error, "Could not load the member")
              : "Member not found in this program."}
        </p>
        <Button variant="outline" asChild>
          <Link to="/perkpoint/member/all">Back to members</Link>
        </Button>
      </div>
    );
  }

  const copyLoginLink = async () => {
    const url = `${window.location.origin}/member/login?memberId=${encodeURIComponent(memberProfile.memberId)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Login link copied");
    } catch {
      toast.error("Could not copy the login link");
    }
  };

  const exportProfile = async () => {
    try {
      const blob = await MembersApi.exportProfile(pid, id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `member-${memberProfile.memberId}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Profile exported");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not export the profile"));
    }
  };

  const tierMeta = getTierMeta(ladder, memberProfile.tier, memberProfile.pointsBalance);
  const currentTierIndex = ladder.findIndex((item) => item.name === memberProfile.tier);
  const selectedTierIndex = ladder.findIndex((item) => item.name === selectedTier);
  const tierChangeDirection =
    selectedTierIndex < 0 || selectedTierIndex === currentTierIndex
      ? null
      : selectedTierIndex > currentTierIndex
        ? "upgrade"
        : "downgrade";

  const activityRows = (activityQuery.data?.items ?? []).map((row) => {
    const meta = ACTIVITY_ICON[row.type] ?? ACTIVITY_ICON.earn;
    return {
      key: row.id,
      date: relativeDays(row.created_at),
      title: row.description || row.txn_code,
      points: `${row.points > 0 ? "+" : ""}${row.points.toLocaleString("en-IN")} pts`,
      positive: row.points > 0,
      category: row.status === "completed" ? meta.category : titleCase(row.status),
      icon: meta.icon,
      iconClass: meta.className,
    };
  });
  const pointsOverviewData = (overviewQuery.data ?? []).map((row) => ({
    month: new Date(`${row.month}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short" }),
    earned: row.earned,
    redeemed: row.redeemed,
  }));
  const rewardsRedeemed = (redemptionsQuery.data ?? []).map((row) => ({
    key: row.txn_code,
    reward: row.reward_name || row.txn_code,
    redeemed: relativeDays(row.created_at),
    points: `${Math.abs(row.points).toLocaleString("en-IN")} pts`,
    reversed: row.reversed,
  }));
  const participation = participationQuery.data;
  const participationRow = (
    label: string,
    icon: typeof Gift,
    item: { count: number; last_at: string | null } | null | undefined,
    noun: string,
  ) => ({
    label,
    icon,
    value: item ? `${item.count} ${noun}` : "Not tracked yet",
    last: item?.last_at ? `Last: ${relativeDays(item.last_at)}` : item ? "None yet" : "Lives outside Perk Point",
  });
  const programParticipation = [
    participationRow("Campaigns", Megaphone, participation?.campaigns, "participated"),
    participationRow("Gamification", Gamepad2, participation?.gamification, "experiences"),
    participationRow("Coupons", Ticket, participation?.coupons, "redeemed"),
  ];
  const expiring = memberProfile.expiringSoon;
  const operating = {
    risk: memberProfile.risk,
    action: memberProfile.nextAction,
    expiringLabel: expiring.points > 0
      ? `${expiring.points.toLocaleString("en-IN")} pts expire by ${formatLongDate(expiring.date)}.`
      : `Nothing expires in the next ${expiring.within_days} days.`,
    activityNote: activityRows.length
      ? `Latest: ${activityRows[0].date.toLowerCase()}, ${activityRows[0].title} (${activityRows[0].points}).`
      : "No points activity yet.",
  };

  const openUpdateTierSheet = () => {
    setSelectedTier(memberProfile.tier);
    setTierChangeReason("");
    setTierSheetOpen(true);
  };

  const openUpdateDetailsSheet = () => {
    setDetailsDraft(detailsDraftFrom(memberProfile));
    setDetailsSheetOpen(true);
  };

  const confirmUpdateDetails = () => {
    if (!detailsDraft) return;
    const name = detailsDraft.name.trim();
    const email = detailsDraft.email.trim();
    const phone = detailsDraft.phone.trim();

    if (!name) {
      toast.error("Enter the member's name");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (detailsDraft.dateOfBirth && isFutureDate(detailsDraft.dateOfBirth)) {
      toast.error("Date of birth cannot be in the future");
      return;
    }
    if (detailsDraft.anniversary && isFutureDate(detailsDraft.anniversary)) {
      toast.error("Anniversary cannot be in the future");
      return;
    }
    if (detailsDraft.consentEmail && !email) {
      toast.error("Add an email before enabling email marketing");
      return;
    }
    if ((detailsDraft.consentWhatsapp || detailsDraft.consentSms) && !phone) {
      toast.error("Add a mobile number before enabling WhatsApp or SMS");
      return;
    }
    updateMutation.mutate(detailsDraft);
  };

  const confirmUpdateTier = () => {
    const target = ladder.find((tier) => tier.name === selectedTier);
    if (!target || selectedTier === memberProfile.tier) {
      toast.error("Select a different tier to update");
      return;
    }
    tierMutation.mutate({ tierKey: target.key, reason: tierChangeReason.trim() });
  };

  return (
    <div className="mx-auto min-w-0 max-w-6xl space-y-8 pb-4">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-[1.75rem] font-semibold tracking-tight text-slate-950 sm:text-4xl">
            {memberProfile.name}
          </h1>
          <p className="mt-2 text-sm text-slate-500 sm:text-base">
            {memberProfile.email || "No email"}
            {memberProfile.phone ? (
              <>
                <span className="mx-1.5 text-slate-300">·</span>
                {memberProfile.phone}
              </>
            ) : null}
            <span className="mx-1.5 text-slate-300">·</span>
            {memberProfile.memberId}
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="h-11 flex-1 rounded-full bg-white sm:flex-none"
              asChild
            >
              <Link to="/perkpoint/member/all">
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                Back to members
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 shrink-0 rounded-full bg-white"
                  aria-label="Member actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => void copyLoginLink()}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy login link
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openUpdateDetailsSheet}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Update member details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openUpdateTierSheet}>
                  <Crown className="mr-2 h-4 w-4" />
                  Update tier
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPointsSheetOpen(true)}>
                  <Coins className="mr-2 h-4 w-4" />
                  Adjust points
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void exportProfile()}>
                  <Download className="mr-2 h-4 w-4" />
                  Export profile
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <section className="overflow-hidden rounded-[28px] bg-[radial-gradient(ellipse_at_top_left,#1C7348,#145C3C_55%,#0E3A28)] text-white shadow-[0_24px_48px_-28px_rgba(14,58,40,0.45)]">
        <div className="grid lg:grid-cols-[1.2fr_0.9fr]">
          <div className="px-6 py-7 sm:px-8 sm:py-9">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-white/10 text-sm font-semibold text-white">
                  {memberProfile.initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-wrap gap-2">
                <Badge className="border-0 bg-white/10 font-medium text-emerald-50 hover:bg-white/10">
                  {memberProfile.tier} tier
                </Badge>
                <Badge
                  className={cn(
                    "border-0 font-medium hover:bg-white/10",
                    operating.risk === "Low risk"
                      ? "bg-white/10 text-emerald-50"
                      : "bg-amber-200/20 text-amber-100",
                  )}
                >
                  {operating.risk}
                </Badge>
                <Badge className="border-0 bg-white/10 font-medium text-emerald-50 hover:bg-white/10">
                  {memberProfile.status} · {memberProfile.engagement} engagement
                </Badge>
              </div>
            </div>
            <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-100/70">
              Available points
            </p>
            <p className="mt-3 flex items-end gap-2 font-semibold tracking-tight">
              <span className="text-5xl leading-none sm:text-6xl">
                {memberProfile.pointsBalance.toLocaleString("en-IN")}
              </span>
              <span className="mb-1 text-lg font-medium text-white/60">
                pts
              </span>
            </p>
            <div className="mt-6 max-w-md">
              <TierProgress
                pointsBalance={memberProfile.pointsBalance}
                pointsToNextTier={tierMeta.pointsToNextTier}
                nextTier={tierMeta.nextTier}
                nextTierPoints={tierMeta.nextTierPoints}
                isTop={tierMeta.isTop}
                tone="dark"
              />
            </div>
          </div>
          <div className="flex flex-col justify-between bg-[#F4F8F5] px-6 py-7 text-slate-950 sm:px-8 sm:py-9">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Next action
              </p>
              <p className="mt-4 text-2xl font-semibold tracking-tight">
                {operating.action}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Last activity {memberProfile.lastActivity.toLowerCase()}. Member
                since {memberProfile.memberSince}. Segment{" "}
                {memberProfile.primarySegment}.
              </p>
            </div>
            <Button
              type="button"
              className="mt-6 h-11 w-full rounded-full bg-[#1B7A45] text-white shadow-none hover:bg-[#16653A] sm:w-auto"
              onClick={openUpdateTierSheet}
            >
              <ArrowUpRight className="mr-1.5 h-4 w-4" />
              Update tier
            </Button>
          </div>
        </div>
      </section>

      <section className="rounded-[24px] bg-white p-5 ring-1 ring-black/[0.06] sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">
            Points story
          </h2>
          <Button
            type="button"
            className="h-10 shrink-0 rounded-full bg-[#1B7A45] text-white shadow-none hover:bg-[#16653A]"
            onClick={() => setPointsSheetOpen(true)}
          >
            <Coins className="mr-1.5 h-4 w-4" />
            Adjust points
          </Button>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          Available, earned, and redeemed are the numbers on this profile.{" "}
          {operating.expiringLabel}
        </p>
        <div className="mt-6 grid gap-6 sm:grid-cols-3">
          <div>
            <p className="text-sm text-slate-500">Available</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
              {memberProfile.pointsBalance.toLocaleString("en-IN")}
              <span className="ml-1 text-base font-medium text-slate-400">
                pts
              </span>
            </p>
            <p className="mt-1 text-sm text-slate-500">Ready to redeem</p>
          </div>
          <div className="border-t border-slate-100 pt-6 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0">
            <p className="text-sm text-slate-500">Earned</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <p className="text-3xl font-semibold tracking-tight text-slate-950">
                {memberProfile.pointsEarned.toLocaleString("en-IN")}
                <span className="ml-1 text-base font-medium text-slate-400">
                  pts
                </span>
              </p>
              {memberProfile.pointsPending > 0 ? (
                <Badge className="border-0 bg-amber-50 font-medium text-amber-800 hover:bg-amber-50">
                  {memberProfile.pointsPending.toLocaleString("en-IN")} pending
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 text-sm text-slate-500">Lifetime</p>
          </div>
          <div className="border-t border-slate-100 pt-6 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0">
            <p className="text-sm text-slate-500">Redeemed</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
              {memberProfile.pointsRedeemed.toLocaleString("en-IN")}
              <span className="ml-1 text-base font-medium text-slate-400">
                pts
              </span>
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Lifetime · {memberProfile.rewardRedemptions} rewards
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-[24px] bg-white p-5 ring-1 ring-black/[0.06] sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-950">
              Profile details
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              Gender, dates, language, city, and consent. Programs and
              campaigns can use these for birthday, anniversary, language, and
              location targeting.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 rounded-full bg-white"
            onClick={openUpdateDetailsSheet}
          >
            <Pencil className="mr-1.5 h-4 w-4" />
            Update details
          </Button>
        </div>
        <dl className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              ["Gender", memberProfile.gender],
              ["Date of birth", formatProfileDate(memberProfile.dateOfBirth)],
              ["Anniversary", formatProfileDate(memberProfile.anniversary)],
              ["City", memberProfile.city],
              ["Preferred language", memberProfile.preferredLanguage],
              ["Marketing consent", consentSummary(memberProfile)],
            ] as const
          ).map(([label, value]) => (
            <div key={label}>
              <dt className="text-sm text-slate-500">{label}</dt>
              <dd className="mt-1 text-sm font-semibold text-slate-950">
                {value || "Not set"}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Activity + loyalty summary */}
      <div className="grid gap-4 xl:grid-cols-5">
        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-3">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold tracking-tight text-slate-950">
                  Recent activity
                </h3>
                <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
                  {operating.activityNote}
                </p>
              </div>
              <Select value={activityType} onValueChange={setActivityType}>
                <SelectTrigger className="h-9 w-[140px] bg-white">
                  <SelectValue placeholder="All activity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All activity</SelectItem>
                  <SelectItem value="earn">Earned</SelectItem>
                  <SelectItem value="redeem">Redeemed</SelectItem>
                  <SelectItem value="adjust">Adjustments</SelectItem>
                  <SelectItem value="expire">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead>Date</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead>Points</TableHead>
                    <TableHead>Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                        {activityQuery.isLoading ? "Loading activity…" : "No activity yet."}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {activityRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="text-slate-600">
                        {row.date}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <div
                            className={cn(
                              "flex h-8 w-8 items-center justify-center rounded-lg",
                              row.iconClass,
                            )}
                          >
                            <row.icon className="h-4 w-4" />
                          </div>
                          <span className="font-medium text-slate-900">
                            {row.title}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          "font-semibold",
                          row.positive ? "text-green-700" : "text-slate-900",
                        )}
                      >
                        {row.points}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className="bg-slate-100 font-medium text-slate-700"
                        >
                          {row.category}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 flex justify-end">
              <Link
                to={`/perkpoint/member/history?member=${id}`}
                className="text-sm font-semibold text-emerald-800"
              >
                View all activity →
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-2">
          <CardContent className="p-5">
            <div className="mb-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Loyalty summary
              </h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {memberProfile.name}&apos;s current loyalty position
              </p>
            </div>

            <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  <Crown className="h-4 w-4 text-amber-500" />
                  Current tier: {memberProfile.tier}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 bg-white"
                  onClick={openUpdateTierSheet}
                >
                  <ArrowUpRight className="mr-1 h-3.5 w-3.5" />
                  Update
                </Button>
              </div>
              <TierProgress
                pointsBalance={memberProfile.pointsBalance}
                pointsToNextTier={tierMeta.pointsToNextTier}
                nextTier={tierMeta.nextTier}
                nextTierPoints={tierMeta.nextTierPoints}
                isTop={tierMeta.isTop}
              />
            </div>

            <div className="mt-5 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white">
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <Calendar className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Member since
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  {memberProfile.memberSince}
                </span>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <Clock className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Last activity
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  {memberProfile.lastActivity}
                </span>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <BarChart3 className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Engagement
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  {memberProfile.engagement}
                </span>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <Gift className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Reward redemptions
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  {memberProfile.rewardRedemptions}
                </span>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <Megaphone className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Campaign participation
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  {memberProfile.campaignParticipation}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 xl:grid-cols-5">
        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-3">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Points overview
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">
                  Points earned and redeemed each month.{" "}
                  {memberProfile.pointsBalance.toLocaleString("en-IN")} pts are
                  available now.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <StartEndDates
                  from={overviewFrom}
                  to={overviewTo}
                  onFrom={setOverviewFrom}
                  onTo={setOverviewTo}
                />
                <ChartExportButton
                  filename={`points-overview-${overviewFrom}-to-${overviewTo}.csv`}
                  rows={pointsOverviewData.map((row) => ({
                    Month: row.month,
                    Earned: row.earned,
                    Redeemed: row.redeemed,
                  }))}
                />
              </div>
            </div>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pointsOverviewData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="earned"
                    name="Points earned"
                    stroke="#1B7A45"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="redeemed"
                    name="Points redeemed"
                    stroke="#86efac"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-2">
          <CardContent className="p-5">
            <div className="mb-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Loyalty behaviour
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">
                This member&apos;s participation counts from their profile.
              </p>
            </div>
            <div className="space-y-3">
              {(
                [
                  {
                    label: "Reward redemptions",
                    value: memberProfile.rewardRedemptions,
                    barClass: "bg-green-600",
                  },
                  {
                    label: "Campaign participation",
                    value: memberProfile.campaignParticipation,
                    barClass: "bg-lime-500",
                  },
                  {
                    label: "Lifetime points earned",
                    value: memberProfile.pointsEarned,
                    barClass: "bg-emerald-500",
                  },
                  {
                    label: "Lifetime points redeemed",
                    value: memberProfile.pointsRedeemed,
                    barClass: "bg-teal-400",
                  },
                ] as const
              ).map((item) => {
                const maxValue = Math.max(
                  1,
                  memberProfile.rewardRedemptions,
                  memberProfile.campaignParticipation,
                  memberProfile.pointsEarned,
                  memberProfile.pointsRedeemed,
                );
                const width = `${Math.min(100, Math.round((item.value / maxValue) * 100))}%`;
                return (
                  <div key={item.label}>
                    <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-slate-700">
                        {item.label}
                      </span>
                      <span className="font-semibold tabular-nums text-slate-800">
                        {item.value.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={cn("h-full rounded-full", item.barClass)}
                        style={{ width }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
          <CardContent className="p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Rewards & redemptions
                </h3>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Rewards {memberProfile.name} has redeemed
                </p>
              </div>
              <Link
                to={`/perkpoint/member/history?member=${id}`}
                className="shrink-0 text-sm font-semibold text-green-700"
              >
                View all →
              </Link>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead>Reward</TableHead>
                    <TableHead>Redeemed</TableHead>
                    <TableHead>Points</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rewardsRedeemed.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                        {redemptionsQuery.isLoading ? "Loading…" : "No redemptions yet."}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {rewardsRedeemed.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="font-medium text-slate-900">
                        {row.reward}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {row.redeemed}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {row.points}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                          <span className={cn("h-2 w-2 rounded-full", row.reversed ? "bg-slate-400" : "bg-green-500")} />
                          {row.reversed ? "Reversed" : "Redeemed"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
          <CardContent className="p-5">
            <div className="mb-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Program participation
              </h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {memberProfile.name}&apos;s participation in the loyalty program
              </p>
            </div>
            <div className="space-y-3">
              {programParticipation.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-xl border border-slate-200 px-3.5 py-3 text-left"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">
                      {item.label}: {item.value}
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {item.last}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
          <CardContent className="p-5">
            <div className="mb-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Member classification
              </h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {memberProfile.name}&apos;s segment and status
              </p>
            </div>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white">
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <Users className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Primary segment
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  {memberProfile.primarySegment}
                </span>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <ShieldCheck className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">Status</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  {memberProfile.status}
                </span>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <Crown className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">Tier</span>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium",
                      tierMeta.badgeClass,
                    )}
                  >
                    <Crown className="h-3.5 w-3.5" />
                    {memberProfile.tier}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-green-700 hover:bg-green-50 hover:text-green-800"
                    onClick={openUpdateTierSheet}
                  >
                    Update
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <BarChart3 className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Engagement
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  {memberProfile.engagement}
                </span>
              </div>
              <div className="flex items-center gap-3 px-3.5 py-3.5">
                <Clock className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 text-sm text-slate-500">
                  Last activity
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  {memberProfile.lastActivity}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Sheet open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
        >
          <div className="flex-1 overflow-y-auto px-5 pb-4 pt-6 sm:px-6">
            <div className="pr-8">
              <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
                Update member details
              </SheetTitle>
              <SheetDescription className="mt-1 text-sm text-muted-foreground">
                These details stay on the member profile so programs and
                campaigns can target birthdays, anniversaries, language, city,
                and consented channels.
              </SheetDescription>
            </div>
            {detailsDraft ? (
              <MemberDetailsForm draft={detailsDraft} onChange={setDetailsDraft} />
            ) : null}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="bg-white"
              onClick={() => setDetailsSheetOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-full bg-[#1B7A45] text-white hover:bg-[#16653A]"
              onClick={confirmUpdateDetails}
              disabled={updateMutation.isPending}
            >
              Save details
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={tierSheetOpen} onOpenChange={setTierSheetOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        >
          <div className="flex-1 overflow-y-auto px-5 pb-4 pt-6 sm:px-6">
            <div className="pr-8">
              <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
                Update tier
              </SheetTitle>
              <SheetDescription className="mt-1 text-sm text-muted-foreground">
                Upgrade or downgrade {memberProfile.name}&apos;s loyalty tier.
              </SheetDescription>
            </div>

            <div className="mt-6 space-y-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs text-muted-foreground">Current tier</p>
                <p className="mt-1 text-base font-semibold text-slate-900">
                  {memberProfile.tier}
                </p>
                {tierChangeDirection ? (
                  <p
                    className={cn(
                      "mt-2 text-xs font-medium",
                      tierChangeDirection === "upgrade"
                        ? "text-green-700"
                        : "text-orange-600",
                    )}
                  >
                    {tierChangeDirection === "upgrade"
                      ? `Upgrading to ${selectedTier}`
                      : `Downgrading to ${selectedTier}`}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="update-tier">New tier</Label>
                <Select
                  value={selectedTier}
                  onValueChange={(value) => setSelectedTier(value)}
                >
                  <SelectTrigger id="update-tier" className="h-10 bg-white">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {ladder.map((tier) => (
                      <SelectItem key={tier.name} value={tier.name}>
                        {tier.name}
                        {tier.name === memberProfile.tier ? " (current)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  Tier ladder
                </p>
                <div className="space-y-2">
                  {ladder.map((tier) => {
                    const tierIndex = ladder.findIndex(
                      (item) => item.name === tier.name,
                    );
                    const isCurrent = tier.name === memberProfile.tier;
                    const isSelected = tier.name === selectedTier;

                    return (
                      <button
                        key={tier.name}
                        type="button"
                        onClick={() => setSelectedTier(tier.name)}
                        className={cn(
                          "flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left text-sm transition-colors",
                          isSelected
                            ? tierIndex < currentTierIndex
                              ? "border-orange-500 bg-orange-50 text-orange-800"
                              : tierIndex > currentTierIndex
                                ? "border-green-600 bg-green-50 text-green-800"
                                : "border-slate-300 bg-slate-50 text-slate-800"
                            : isCurrent
                              ? "border-slate-200 bg-slate-50 text-slate-700"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                        )}
                      >
                        <span className="font-medium">{tier.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {isCurrent
                            ? "Current"
                            : isSelected
                              ? tierIndex > currentTierIndex
                                ? "Upgrade"
                                : "Downgrade"
                              : `${tier.threshold.toLocaleString("en-IN")}+ pts`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tier-change-reason">Reason (optional)</Label>
                <Textarea
                  id="tier-change-reason"
                  value={tierChangeReason}
                  onChange={(e) => setTierChangeReason(e.target.value)}
                  placeholder="e.g. Manual adjustment for retention / policy"
                  className="min-h-[90px] bg-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="bg-white"
              onClick={() => setTierSheetOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={cn(
                "rounded-full text-white",
                tierChangeDirection === "downgrade"
                  ? "bg-amber-700 hover:bg-amber-800"
                  : "bg-[#1B7A45] hover:bg-[#16653A]",
              )}
              disabled={!tierChangeDirection || tierMutation.isPending}
              onClick={confirmUpdateTier}
            >
              <ArrowUpRight className="mr-1.5 h-4 w-4" />
              {tierChangeDirection === "downgrade"
                ? "Confirm downgrade"
                : tierChangeDirection === "upgrade"
                  ? "Confirm upgrade"
                  : "Confirm update"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AdjustMemberPointsDialog
        open={pointsSheetOpen}
        onOpenChange={setPointsSheetOpen}
        programId={pid}
        memberId={id}
        memberName={memberProfile.name}
        available={memberProfile.pointsBalance}
        onAdjusted={() => refreshMember()}
      />
    </div>
  );
}
